import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSource } from "@/context/SourceContext";

const norm = (o) => (typeof o === "string" ? { value: o, label: o } : o);

export const MultiSelect = ({ field, label, value, onChange, testid, endpoint = "/sales/options", staticOptions, chips = true, extraQuery = [] }) => {
  const src = useSource();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const companies = extraQuery.join(",");
  useEffect(() => {
    if (!open || staticOptions) return undefined;
    const t = setTimeout(() => {
      const p = new URLSearchParams({ field, q });
      if (companies) companies.split(",").forEach((c) => p.append("company", c));
      src.get(`${endpoint}?${p}`).then((r) => setOptions(r.data.options.map(norm))).catch(() => setOptions([]));
    }, 200);
    return () => clearTimeout(t);
  }, [src, field, q, open, endpoint, staticOptions, companies]);
  const shown = staticOptions ? staticOptions.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  const toggle = (name) => onChange(value.includes(name) ? value.filter((v) => v !== name) : [...value, name]);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 gap-1.5 bg-white text-xs" data-testid={`${testid}-trigger`}>
            {label}{value.length ? ` (${value.length})` : ""} <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder={`Search ${label.toLowerCase()}…`} value={q} onValueChange={setQ} data-testid={`${testid}-search`} />
            <CommandList>
              <CommandEmpty>No matches</CommandEmpty>
              <CommandGroup>
                {shown.map((o) => (
                  <CommandItem key={o.value} value={o.value} onSelect={() => toggle(o.value)} data-testid={`${testid}-option-${o.value}`}>
                    <Check className={`mr-2 h-4 w-4 ${value.includes(o.value) ? "opacity-100" : "opacity-0"}`} /> {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {chips && value.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-800" data-testid={`${testid}-chip`}>
          {v}<button type="button" onClick={() => toggle(v)} aria-label={`Remove ${v}`} data-testid={`${testid}-chip-remove`}><X className="h-3 w-3" /></button>
        </span>
      ))}
    </div>
  );
};
